/**
 * @module @anydo/vendor/sentry
 */
// spellchecker:ignore trackjs

//#region Imports
import type { SeverityLevel } from "@sentry/types";
import type { IAnydoSentry } from "@anydo/interfaces";
import { SentryAdapter } from "./-private/sentry-adapter";
//#endregion

//#region types
type AppStoreT = "stripe" | "apple";

type EnvironmentT = "production" | "dev" | "beta" | "local" | "none" | "NETLIFY";

type LevelT = undefined | SeverityLevel;
//#endregion

//#region Config
const NOT_INITIALIZED_ID = "SENTRY_NOT_INITIALIZED_ID";

const SENTRY_CRUSH_REPORTER_SUBMIT_URL = "https://sentry.io/api/2404976/minidump/?sentry_key=881ed2442ad94d8f91be537d9dc6120d"; // prettier-ignore
export const SENTRY_DSN = "https://881ed2442ad94d8f91be537d9dc6120d@sentry.io/2404976";

const VALID_ENVIRONMENTS = new Set<EnvironmentT>(["production", "local", "none", "NETLIFY", "beta", "dev"]);
const ENVIRONMENT = process.env.REACT_APP_SENTRY_ENVIRONMENT as EnvironmentT;

const APP_STORE: AppStoreT = process.env.REACT_APP_PLATFORM_INSTALLER === "mac store" ? "apple" : "stripe";

const PACKAGE = process.env.REACT_APP_SENTRY_PACKAGE;
export const RELEASE = `${PACKAGE}@${getAppVersion()}`;

const DENY_URLS = [
  // Analytics URLs
  /\.amazonaws\.com/i,
  /capture\.trackjs\.com/i,
];

const IGNORE_ERRORS = [
  // Webpack
  "ChunkLoadError",
  "Loading CSS chunk",
  // 3rd party Extensions
  "_avast_submit",
  "Cannot set property 'install' of undefined",
  "chrome is not defined",
  "chrome-extension://",
  "chrome-extension",
  "moz-extension://",
  "moz-extension",
  "Extension context invalidated",
  // Unique platform
  "window.__pad.performLoop", // Unknown what is related to
  "Cannot read property 'contentWindow' of null", // Unique platform
  "Cannot read property 'dataset' of null", // Unique platform (Some Electron user, issue: 1535245776)
  "RamboxPro", // Unique platform (RamboxPro)
  "webkit.messageHandlers.contextMenuMessageHandler", // Unique platform (Apple mail)
];
//#endregion

export const AnydoSentry: IAnydoSentry = {
  /**
   * @see https://docs.sentry.io/platforms/javascript/electron/#electron-native-manual
   */
  SENTRY_CRUSH_REPORTER_SUBMIT_URL,

  /**
   * If it's called on Electron's prerender, it should be called before
   * `AnydoElectronPreload` exist.
   */
  init(options) {
    if (ENVIRONMENT === "none") return;

    if (!VALID_ENVIRONMENTS.has(ENVIRONMENT)) {
      throw new Error(`REACT_APP_SENTRY_ENVIRONMENT isn't valid, got '${ENVIRONMENT}'`);
    }

    SentryAdapter.init({
      enabled: true,
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: RELEASE,
      denyUrls: DENY_URLS,
      ignoreErrors: IGNORE_ERRORS,

      beforeBreadcrumb(breadcrumb) {
        // Don't add breadcrumb of ignored URLs
        if (breadcrumb.category === "xhr") {
          if (breadcrumb.data && typeof breadcrumb.data.url === "string") {
            const url = breadcrumb.data.url;
            if (DENY_URLS.some((re) => re.test(url))) {
              return null;
            }
          }
        }

        return breadcrumb;
      },

      beforeSend(event, hint) {
        // Remove Chrome / Firefox extensions emitted errors
        if (hint != null && hint.originalException != null) {
          const originalException: any = hint.originalException;

          if (typeof originalException === "string" && originalException.includes("extension:")) return null;

          if (originalException.stack) {
            const stack = String(originalException.stack);
            if (stack.includes("extension:")) return null;
            if (stack.startsWith("FirebaseError")) return null;
          }
        }

        return event;
      },
    });

    //
    SentryAdapter.setTag("app_store", APP_STORE);
    //
    SentryAdapter.setTag("app_os", options.app_os);
    SentryAdapter.setTag("app_platform", options.app_platform);
    SentryAdapter.setTag("app_window", options.app_window);

    if (typeof LogRocket !== "undefined") {
      LogRocket.getSessionURL((sessionUrl) => {
        AnydoSentry.addBreadcrumb({
          message: "LogRocket Session",
          category: "logrocket",
          level: "info",
          data: { sessionUrl },
        });
      });
    }
  },

  //#region User
  setUserLogin(options) {
    if (ENVIRONMENT === "none") return;

    SentryAdapter.setUser({
      id: options.id,
      email: options.email,
    });
  },

  setUserLogout() {
    if (ENVIRONMENT === "none") return;
    SentryAdapter.setUser(null);
  },
  //#endregion

  //#region Meta data
  setTag(key, value) {
    if (ENVIRONMENT === "none") return;
    SentryAdapter.setTag(key, value);
  },

  setExtra(key, value) {
    if (ENVIRONMENT === "none") return;
    SentryAdapter.setExtra(key, value);
  },
  //#endregion

  //#region Captures
  captureMessage(message, level) {
    if (ENVIRONMENT === "none") return NOT_INITIALIZED_ID;
    return SentryAdapter.captureMessage(message, level as LevelT);
  },

  captureException(exception) {
    if (ENVIRONMENT === "none") return NOT_INITIALIZED_ID;
    return SentryAdapter.captureException(exception);
  },

  withScope(callback) {
    if (ENVIRONMENT === "none") return NOT_INITIALIZED_ID;
    return SentryAdapter.withScope(callback);
  },

  lastEventId() {
    return SentryAdapter.lastEventId();
  },
  //#endregion

  //#region Breadcrumbs
  addBreadcrumb(breadcrumb) {
    if (ENVIRONMENT === "none") return;

    SentryAdapter.addBreadcrumb({
      type: breadcrumb.type,
      level: breadcrumb.level as LevelT,
      event_id: breadcrumb.event_id,
      category: breadcrumb.category,
      message: breadcrumb.message,
      data: breadcrumb.data,
      timestamp: breadcrumb.timestamp,
    });
  },
  //#endregion
};

//#region Helpers
function getAppVersion(): string {
  // NOTE: For some reason in tests `null` is `"null"`
  // `REACT_APP_ANYDO_VERSION` doesn't exist in Storybook
  if (process.env.STORYBOOK_STORYBOOK != null && process.env.STORYBOOK_STORYBOOK !== "null") {
    return "4.0.0-local";
  } else {
    // We ensure REACT_APP_ANYDO_VERSION existence in local builds: `@anydo/anydo-scripts/src/utils/getClientEnvironment.js`
    if (!process.env.REACT_APP_ANYDO_VERSION) {
      throw new Error("process.env.REACT_APP_ANYDO_VERSION must be set");
    }

    return process.env.REACT_APP_ANYDO_VERSION;
  }
}
//#endregion
